import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import { Input, Spinner } from "reactstrap";
import { useDispatch } from "react-redux";
import { login } from "../../../actions/customer";
import { CustomSnackbar } from "../../../helpers/CustomSnackbar";
import PasswordInput from "../../../helpers/Common/PasswordInput";
import { isEmailVerified, isMobileVerified, redirect_url_key, detectLoginMethod, last_logged_in_username_key } from "../../../helpers/common";
import VerifyEmailAndMobileModal from "./VerifyEmailAndMobileModal";

const SignInForm = ({ formData, setFormData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openVerifyModal, setOpenVerifyModal] = useState(false);
  const [userData, setUserData] = useState(null);

  const [searchParams] = useSearchParams();

  // `/signup?isInternal=true` this url is for our internal members to create new accounts and to bypass the email verification step.
  const isInternal = searchParams.get("isInternal");

  useEffect(() => {
    const username = localStorage.getItem(last_logged_in_username_key);

    if (username) {
      setFormData((prev) => ({ ...prev, username }));
    }
  }, [setFormData]);

  const onValueChange = (e, name) => {
    setFormData({ ...formData, [name]: e.target.value });
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!formData?.username) {
      CustomSnackbar.warning("Please enter your email or mobile number");
      return;
    }

    const loginMethod = detectLoginMethod(formData?.username);
    if (!loginMethod) {
      CustomSnackbar.warning("Please enter a valid email address or mobile number");
      return;
    }

    if (formData?.password?.length < 8) {
      CustomSnackbar.warning("Password should be at least 8 characters");
      return;
    }

    // Store username for last logged in (both email and mobile)
    localStorage.setItem(last_logged_in_username_key, formData?.username);
    localStorage.setItem("customer_type", formData.customer_type);

    const redirect_url = localStorage.getItem(redirect_url_key);

    setIsLoading(true);

    // Prepare login payload with detected method
    const loginPayload = {
      username: formData?.username,
      password: formData?.password,
      login_method: loginMethod,
      customer_type: formData?.customer_type
    };

    dispatch(login(loginPayload, isInternal)).then((res) => {
      setIsLoading(false);

      // If the user is not verified (email or mobile), then showing popup to verify their accounts first.
      if (res?.data && (!isEmailVerified(res?.data) || !isMobileVerified(res?.data)) && !isInternal) {
        setUserData(res?.data);
        setOpenVerifyModal(true);
        return;
      }

      if (res?.data?.customer?.customer_type === formData?.customer_type) {
        if (redirect_url) {
          navigate(redirect_url);
          localStorage.removeItem(redirect_url_key);
          return false;
        }

        if (formData?.customer_type === 1) {
          navigate("/employer/company/manage");
        } else {
          navigate(`/candidate/profile/update${isInternal ? '?isInternal=true' : ''}`);
        }
      }
    });
  };

  return (
    <>
      <VerifyEmailAndMobileModal
        openModal={openVerifyModal}
        setOpenModal={setOpenVerifyModal}
        formData={formData}
        userData={userData}
      />

      <Form onSubmit={onSubmit} className='auth-form'>
        <div className='mb-3'>
          <label htmlFor='usernameInput' className='form-label'>
            Email or Mobile Number
          </label>
          <Input
            type='text'
            className='form-control'
            id='usernameInput'
            placeholder='Enter your email or mobile number'
            required
            name='username'
            onChange={(e) => onValueChange(e, "username")}
            value={formData?.username}
          />
        </div>

        <div className='mb-3'>
          <PasswordInput
            label='Password'
            id='passwordInput'
            name='password'
            required={true}
            value={formData?.password}
            placeholder='Enter your password'
            onChange={(e) => onValueChange(e, "password")}
          />
        </div>
        <div className='mb-4'>
          <div className='form-check'>
            <Link to='/resetpassword' className='float-end text-white'>
              Forgot Password?
            </Link>
          </div>
        </div>
        <div className='text-center'>
          {isLoading ? (
            <button
              className='btn btn-white btn-hover w-100 disabled'
              type='button'
            >
              Sign In <Spinner size='sm' />
            </button>
          ) : (
            <button className='btn btn-white btn-hover w-100' type='submit'>
              Sign In
            </button>
          )}
        </div>
      </Form>
    </>
  );
};

export default SignInForm;
